import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

// Native pixel geometry of the Figma lockup. Everything below is expressed as a
// fraction of these so the whole lockup scales as one unit on narrow screens.
const LOCKUP_W = 370;
const LOCKUP_H = 87;
const ICON = 87;
const GAP = 12;
const WORDMARK_W = LOCKUP_W - ICON - GAP; // 271, the wordmark PNG's native width
const WORDMARK_TOP = 11; // wordmark box top, measured from the icon box top
const TAGLINE_BOTTOM = 2; // tagline baseline box bottom, measured from the icon box bottom
const TAGLINE_FONT = 14;
const WORDMARK_PAD_RIGHT = 6; // clear pixels between the last glyph and the PNG edge

const pct = (value: number, of: number) => `${(value / of) * 100}%`;

/**
 * ConXedge lockup as laid out in the Figma auth screens: the X icon on the
 * left, the wordmark to its right, and the tagline set in Inter beneath the
 * wordmark, right-aligned. Renders at the Figma's native 370px and shrinks
 * proportionally (icon, wordmark and tagline together) when narrower.
 */
const AuthLogo: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: LOCKUP_W,
        containerType: 'inline-size',
        // The lockup is 10px wider than the form; on desktop it hangs that far
        // into the panel padding so the wordmark stays flush with the fields.
        ml: { md: '-10px' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch', aspectRatio: `${LOCKUP_W} / ${LOCKUP_H}` }}>
        <Box
          component="img"
          src="/logo/conxedge-x.png"
          alt=""
          aria-hidden
          sx={{ width: pct(ICON, LOCKUP_W), height: '100%', flex: 'none', display: 'block' }}
        />
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            ml: pct(GAP, LOCKUP_W),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
          }}
        >
          <Box
            component="img"
            src="/logo/conxedge-wordmark.png"
            alt="ConXedge"
            sx={{ mt: pct(WORDMARK_TOP, WORDMARK_W), width: '100%', height: 'auto', display: 'block' }}
          />
          <Typography
            sx={{
              mt: 'auto',
              mb: pct(TAGLINE_BOTTOM, WORDMARK_W),
              // The wordmark PNG has 6px of clear space on its right; align the
              // tagline to the glyphs, not the image edge.
              mr: pct(WORDMARK_PAD_RIGHT, WORDMARK_W),
              fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 400,
              fontSize: `clamp(8px, ${(TAGLINE_FONT / LOCKUP_W) * 100}cqw, ${TAGLINE_FONT}px)`,
              lineHeight: 1,
              // Inter renders a hair wider than the Figma export; this brings the
              // tagline to the wordmark's glyph width (259px at full size).
              letterSpacing: '-0.014em',
              whiteSpace: 'nowrap',
              color: '#8b8489',
              textAlign: 'right',
            }}
          >
            {t('login.tagline')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default AuthLogo;
